/**
 * Main App Layout with Collapsible Sidebar for Admin/Master
 */
import React, { useMemo, useState } from 'react';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Divider,
  Tooltip,
  Avatar,
  Typography,
  Chip,
  useMediaQuery,
  useTheme,
  AppBar,
  Toolbar,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Chat as ChatIcon,
  Dashboard as DashboardIcon,
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Logout as LogoutIcon,
  AdminPanelSettings as AdminIcon,
  AutoAwesome as SparkleIcon,
  CloudUpload,
  Language as LanguageIcon,
  Settings as SettingsIcon,
  Extension as ExtensionIcon,
  SettingsApplications as MCPConfigIcon,
  Article as LogIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import useAuthStore from '../store/authStore';
import { useTranslation } from 'react-i18next';

const DRAWER_WIDTH_EXPANDED = 260;
const DRAWER_WIDTH_COLLAPSED = 72;

export default function AppLayout({ children }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuthStore();
  const { t, i18n } = useTranslation(['common', 'settings']);

  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [languageMenuAnchorEl, setLanguageMenuAnchorEl] = useState(null);

  const drawerWidth = collapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH_EXPANDED;
  const currentLanguage = (i18n.resolvedLanguage || i18n.language || 'vi').split('-')[0];

  const languageOptions = useMemo(
    () => [
      {
        code: 'vi',
        label: t('nav.languageOptions.vi'),
        flag: '🇻🇳',
      },
      {
        code: 'en',
        label: t('nav.languageOptions.en'),
        flag: '🇺🇸',
      },
      {
        code: 'ja',
        label: t('nav.languageOptions.ja'),
        flag: '🇯🇵',
      },
    ],
    [t]
  );

  const isLanguageMenuOpen = Boolean(languageMenuAnchorEl);
  const currentLanguageOption = languageOptions.find((option) => option.code === currentLanguage);

  const handleLanguageMenuOpen = (event) => {
    setLanguageMenuAnchorEl(event.currentTarget);
  };

  const handleLanguageMenuClose = () => {
    setLanguageMenuAnchorEl(null);
  };

  const handleSelectLanguage = (lang) => {
    if (lang) {
      i18n.changeLanguage(lang);
    }
    handleLanguageMenuClose();
  };

  const handleToggleDrawer = () => {
    if (isMobile) {
      setMobileOpen(!mobileOpen);
    } else {
      setCollapsed(!collapsed);
    }
  };

  const handleNavigate = (path) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const menuItems = useMemo(() => {
    const items = [
      {
        title: t('nav.chat'),
        path: '/chat',
        icon: <ChatIcon />,
        color: '#2196f3',
        gradient: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
      },
      {
        title: t('nav.dashboard'),
        path: '/admin',
        icon: <DashboardIcon />,
        color: '#9c27b0',
        gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      },
      {
        title: t('nav.rag'),
        path: '/rag',
        icon: <CloudUpload />,
        color: '#f57c00',
        gradient: 'linear-gradient(135deg, #f57c00 0%, #ff6f00 100%)',
      },
    ];

    // Add Settings menu only for MASTER role
    if (user?.role === 'MASTER') {
      items.push({
        title: t('nav.settings'),
        path: '/settings',
        icon: <SettingsIcon />,
        color: '#00897b',
        gradient: 'linear-gradient(135deg, #00897b 0%, #00695c 100%)',
      });
      items.push({
        title: t('settings:mcpConfiguration'),
        path: '/settings/mcp-configuration',
        icon: <MCPConfigIcon />,
        color: '#7b1fa2',
        gradient: 'linear-gradient(135deg, #7b1fa2 0%, #6a1b9a 100%)',
      });
      items.push({
        title: t('nav.logs', 'Log Viewer'),
        path: '/settings/logs',
        icon: <LogIcon />,
        color: '#546e7a',
        gradient: 'linear-gradient(135deg, #546e7a 0%, #37474f 100%)',
      });
    }

    return items;
  }, [t, user]);

  const isActive = (path) => location.pathname === path;

  const DrawerContent = () => (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: 'linear-gradient(180deg, #1a1a2e 0%, #16213e 100%)',
        color: 'white',
        transition: 'all 0.3s ease',
      }}
    >
      {/* Header with Logo */}
      <Box
        sx={{
          p: 2.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: collapsed ? 1 : 1.5 }}>
          <Avatar
            sx={{
              width: 40,
              height: 40,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
            }}
          >
            <SparkleIcon />
          </Avatar>
          {!collapsed && (
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
                {t('brand.name')}
              </Typography>
              <Chip
                icon={<AdminIcon sx={{ fontSize: '0.8rem !important' }} />}
                label={user?.role}
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.7rem',
                  bgcolor: 'rgba(255, 215, 0, 0.2)',
                  color: '#ffd700',
                  border: '1px solid rgba(255, 215, 0, 0.3)',
                  fontWeight: 600,
                  '& .MuiChip-icon': {
                    color: '#ffd700',
                  },
                }}
              />
            </Box>
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title={t('nav.language')} placement={collapsed ? 'right' : 'bottom'} arrow>
            <IconButton
              onClick={handleLanguageMenuOpen}
              size="small"
              sx={{
                color: 'white',
                bgcolor: 'rgba(255,255,255,0.1)',
                position: 'relative',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.2)',
                  transform: 'scale(1.05)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              <LanguageIcon fontSize="small" />
              {currentLanguageOption && (
                <Typography
                  component="span"
                  sx={{
                    position: 'absolute',
                    bottom: -4,
                    right: -4,
                    fontSize: '0.75rem',
                    lineHeight: 1,
                  }}
                >
                  {currentLanguageOption.flag}
                </Typography>
              )}
            </IconButton>
          </Tooltip>

          {!isMobile && (
            <IconButton
              onClick={handleToggleDrawer}
              size="small"
              sx={{
                color: 'white',
                bgcolor: 'rgba(255, 255, 255, 0.1)',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                  transform: 'scale(1.1)',
                },
                transition: 'all 0.2s',
              }}
            >
              {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
            </IconButton>
          )}
        </Box>
      </Box>

      <Menu
        anchorEl={languageMenuAnchorEl}
        open={isLanguageMenuOpen}
        onClose={handleLanguageMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        keepMounted
        MenuListProps={{
          sx: {
            display: 'flex',
            flexDirection: 'column',
            px: 1,
            py: 1,
            gap: 1,
          },
        }}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 12px 32px rgba(0,0,0,0.25)',
            minWidth: 0,
          },
        }}
      >
        {languageOptions.map((option) => (
          <MenuItem
            key={option.code}
            onClick={() => handleSelectLanguage(option.code)}
            selected={option.code === currentLanguage}
            sx={{
              borderRadius: 1.5,
              gap: 1,
              px: 1.5,
              py: 1,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Typography component="span" fontSize="1.6rem" lineHeight={1}>
              {option.flag}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontWeight: option.code === currentLanguage ? 600 : 500,
                minWidth: 80,
              }}
            >
              {option.label}
            </Typography>
          </MenuItem>
        ))}
      </Menu>

      {/* Navigation Menu */}
      <List sx={{ flexGrow: 1, px: 1.5, py: 2 }}>
        {menuItems.map((item) => {
          const active = isActive(item.path);
          return (
            <Tooltip
              key={item.path}
              title={collapsed ? item.title : ''}
              placement="right"
              arrow
            >
              <ListItem disablePadding sx={{ mb: 1 }}>
                <ListItemButton
                  onClick={() => handleNavigate(item.path)}
                  sx={{
                    borderRadius: 2,
                    minHeight: 48,
                    justifyContent: collapsed ? 'center' : 'flex-start',
                    px: collapsed ? 0 : 2.5,
                    background: active ? item.gradient : 'transparent',
                    boxShadow: active ? `0 4px 12px ${item.color}40` : 'none',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      background: active
                        ? item.gradient
                        : 'rgba(255, 255, 255, 0.1)',
                      transform: 'translateX(4px)',
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: collapsed ? 'auto' : 40,
                      color: 'white',
                      justifyContent: 'center',
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  {!collapsed && (
                    <ListItemText
                      primary={item.title}
                      primaryTypographyProps={{
                        fontWeight: active ? 600 : 500,
                        fontSize: '0.95rem',
                      }}
                    />
                  )}
                </ListItemButton>
              </ListItem>
            </Tooltip>
          );
        })}
      </List>

      <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)' }} />

      {/* User Profile Section */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'space-between',
          gap: 1.5,
          bgcolor: 'rgba(255, 255, 255, 0.05)',
        }}
      >
        {!collapsed && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar
              sx={{
                width: 36,
                height: 36,
                bgcolor: '#2196f3',
                fontSize: '0.9rem',
              }}
            >
              {user?.full_name?.charAt(0)?.toUpperCase()}
            </Avatar>
            <Box sx={{ overflow: 'hidden' }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {user?.full_name}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: 'rgba(255, 255, 255, 0.6)',
                  fontSize: '0.7rem',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {user?.email}
              </Typography>
            </Box>
          </Box>
        )}

        <Tooltip title={collapsed ? t('nav.logout') : ''} placement="right" arrow>
          <IconButton
            onClick={handleLogout}
            size="small"
            sx={{
              color: 'white',
              bgcolor: 'rgba(244, 67, 54, 0.2)',
              '&:hover': {
                bgcolor: 'rgba(244, 67, 54, 0.3)',
                transform: 'scale(1.1)',
              },
              transition: 'all 0.2s',
            }}
          >
            <LogoutIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', height: '100vh' }}>
      {/* Mobile App Bar */}
      {isMobile && (
        <AppBar
          position="fixed"
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          }}
        >
          <Toolbar>
            <IconButton
              color="inherit"
              edge="start"
              onClick={handleToggleDrawer}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 700 }}>
              {t('brand.name')}
            </Typography>
          </Toolbar>
        </AppBar>
      )}

      {/* Sidebar Drawer */}
      <Drawer
        variant={isMobile ? 'temporary' : 'permanent'}
        open={isMobile ? mobileOpen : true}
        onClose={() => setMobileOpen(false)}
        ModalProps={{
          keepMounted: true, // Better mobile performance
        }}
        sx={{
          width: isMobile ? DRAWER_WIDTH_EXPANDED : drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: isMobile ? DRAWER_WIDTH_EXPANDED : drawerWidth,
            boxSizing: 'border-box',
            border: 'none',
            transition: 'width 0.3s ease',
            overflowX: 'hidden',
          },
        }}
      >
        <DrawerContent />
      </Drawer>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: {
            xs: '100%',
            md: `calc(100% - ${drawerWidth}px)`,
          },
          height: '100vh',
          overflow: 'hidden',
          bgcolor: '#f5f7fa',
          transition: 'all 0.3s ease',
          mt: isMobile ? '64px' : 0,
        }}
      >
        {children}
      </Box>
    </Box>
  );
}


